/**
 * CrosswordIntroModal — welcome / continue / view-results modal shown before
 * the crossword grid is revealed.
 *
 * Modes (chosen by parent based on the guest's server-side attempt state):
 *   - "welcome"  : never started — full rules + Begin
 *   - "continue" : previously started, not completed — Continue
 *   - "results"  : already completed & valid — View Results
 *
 * Focus is trapped, background is inert, Escape closes safely. Opening or
 * closing never consumes the guest's one official attempt.
 */
import { useEffect, useRef } from "react";
import { X, Trophy, Sparkles } from "lucide-react";
import { T } from "../lib/i18n";

const RULES = [
  "You get one official attempt",
  "Your timer starts when you press Begin",
  "Complete as many clues as you can",
  "You may submit a partially completed puzzle",
];

const SCORING = [
  "Correct answers earn points",
  "Hints reduce your score",
  "Your final point total goes toward the leaderboard",
  "The guest at the top of the leaderboard wins a prize",
];

export default function CrosswordIntroModal({
  mode = "welcome",       // "welcome" | "continue" | "results"
  brideName = "Aimee",
  groomName = "Riyaz",
  prizeText = "Top the leaderboard and win a prize.",
  onClose,
  onPrimary,              // handler for Begin / Continue / View Results
  primaryLoading = false,
}) {
  const dialogRef = useRef(null);
  const primaryRef = useRef(null);

  // Escape closes; lock scroll; trap focus; auto-focus the primary action.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === "Tab") {
        const focusables = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    // Restore focus after a tick
    setTimeout(() => primaryRef.current?.focus(), 30);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const isContinue = mode === "continue";
  const isResults = mode === "results";

  const title = isContinue
    ? T("Continue the Couple's Crossword")
    : isResults
      ? T("Your Crossword is Submitted")
      : T("Welcome to the Couple's Crossword");

  const primaryLabel = isContinue ? T("Continue Crossword") : isResults ? T("View Results") : T("Begin Crossword");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 fadein p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crossword-intro-title"
      data-testid="crossword-intro-modal"
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden slide-up flex flex-col max-h-[85vh]"
        style={{ background: "var(--motif-surface)" }}
      >
        {/* Mobile grabber */}
        <div className="sm:hidden pt-2 pb-1 flex justify-center flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-black/20" />
        </div>

        <div className="relative px-6 pt-5 pb-1 text-center">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition"
            style={{ background: "var(--motif-accent)" }}
            aria-label="Close introduction"
            data-testid="crossword-intro-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center justify-center gap-2 text-primary-motif text-[11px] tracking-luxe">
            <Sparkles className="w-3.5 h-3.5" />
            {isContinue ? T("IN PROGRESS") : isResults ? T("COMPLETED") : T("COUPLE'S CROSSWORD")}
          </div>
          <h2
            id="crossword-intro-title"
            className="font-display text-2xl sm:text-3xl text-motif leading-tight mt-1"
          >
            {title}
          </h2>
          {!isContinue && !isResults && (
            <p className="text-sm text-motif-muted mt-2 leading-snug">
              {T("How well do you know")} {brideName} {T("and")} {groomName}? {T("Do as much as you can, then submit your crossword when you are ready. You do not need to complete every clue — correct partial answers still earn points toward the leaderboard and the prize.")}
            </p>
          )}
          {isContinue && (
            <p className="text-sm text-motif-muted mt-2 leading-snug">
              {T("Complete as much as you can, then submit when you are ready. Correct partial answers still earn leaderboard points.")}
            </p>
          )}
          {isResults && (
            <p className="text-sm text-motif-muted mt-2 leading-snug">
              {T("You have completed your one official attempt. Tap below to see your score and leaderboard position.")}
            </p>
          )}
        </div>

        <div className="px-6 pb-5 overflow-y-auto space-y-4 text-center">
          {!isContinue && !isResults && (
            <>
              {/* Prize callout */}
              <div
                className="rounded-2xl p-4 flex items-center gap-3 justify-center"
                style={{ background: "var(--motif-accent)" }}
                data-testid="crossword-intro-prize"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--motif-primary)", color: "var(--motif-primary-fg)" }}
                >
                  <Trophy className="w-5 h-5" />
                </div>
                <div className="text-sm text-motif leading-snug text-left">
                  <span className="text-primary-motif font-medium">🏆 </span>
                  {T(prizeText)}
                </div>
              </div>

              {/* Rules */}
              <section>
                <div className="text-[10px] tracking-luxe text-primary-motif mb-2">{T("RULES")}</div>
                <ul className="space-y-1.5 text-sm text-motif inline-block text-left" data-testid="crossword-intro-rules">
                  {RULES.map((r) => (
                    <li key={r} className="flex items-start gap-2">
                      <span className="text-primary-motif mt-0.5">•</span>
                      <span className="leading-snug">{T(r)}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Scoring summary */}
              <section>
                <div className="text-[10px] tracking-luxe text-primary-motif mb-2">{T("SCORING")}</div>
                <ul className="space-y-1.5 text-sm text-motif-muted inline-block text-left" data-testid="crossword-intro-scoring">
                  {SCORING.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <span className="text-primary-motif mt-0.5">•</span>
                      <span className="leading-snug">{T(s)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>

        {/* Actions */}
        <div
          className="p-4 flex flex-col gap-2 border-t"
          style={{ borderColor: "var(--motif-border)" }}
        >
          <button
            ref={primaryRef}
            onClick={onPrimary}
            disabled={primaryLoading}
            className="btn-primary w-full text-sm disabled:opacity-60"
            data-testid="crossword-intro-primary"
          >
            {primaryLoading ? T("Starting…") : primaryLabel}
          </button>
          {!isResults && (
            <button
              onClick={onClose}
              className="btn-ghost w-full text-xs"
              data-testid="crossword-intro-not-yet"
            >
              {T("Not Yet")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
